import React, { useState, useMemo } from "react";
import { Collapse, Empty, Tag, Space, Tooltip, Badge } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  FormOutlined,
  PhoneOutlined,
  NumberOutlined,
  DownOutlined,
  CalendarOutlined,
  FileOutlined,
  LockOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { showConfirmDlgWidget } from "components/ConfirmDlgWidget/showConfirmDlgWidget";
import "./FormUiGroupedView.css";

const { Panel } = Collapse;

// Map UI type codes to readable labels and icons
const UI_TYPE_MAP = {
  "1": { label: "Text", icon: <FormOutlined /> },
  "2": { label: "Number", icon: <NumberOutlined /> },
  "3": { label: "Dropdown", icon: <DownOutlined /> },
  "4": { label: "Phone", icon: <PhoneOutlined /> },
  "5": { label: "Button", icon: <FileOutlined /> },
  "6": { label: "Date", icon: <CalendarOutlined /> },
  "7": { label: "Date Input", icon: <CalendarOutlined /> },
};

// Group type order for sorting
const GROUP_TYPE_ORDER = {
  "1": 1, // Participant Info
  "2": 2, // Parent/Guardian Info
  "3": 3, // Emergency Contact
  "4": 4, // Other Info
};

function FormUiGroupedView({ dataList, deleteData, updateData, onAddToGroup }) {
  const [activeKeys, setActiveKeys] = useState([]);

  // Group fields by group_title and sort
  const groupedData = useMemo(() => {
    if (!dataList || dataList.length === 0) return [];

    // Group by group_title
    const groups = {};
    dataList.forEach((item) => {
      const groupKey = item.group_title || "Other";
      if (!groups[groupKey]) {
        groups[groupKey] = {
          title: groupKey,
          groupType: item.group_type,
          fields: [],
        };
      }
      groups[groupKey].fields.push(item);
    });

    // Sort fields within each group by order
    Object.values(groups).forEach((group) => {
      group.fields.sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
    });

    // Convert to array and sort groups by group_type
    return Object.values(groups).sort((a, b) => {
      const orderA = GROUP_TYPE_ORDER[a.groupType] || 99;
      const orderB = GROUP_TYPE_ORDER[b.groupType] || 99;
      return orderA - orderB;
    });
  }, [dataList]);

  // Expand all groups by default
  React.useEffect(() => {
    if (groupedData.length > 0 && activeKeys.length === 0) {
      setActiveKeys(groupedData.map((g) => g.title));
    }
  }, [groupedData]);

  const handleDelete = async (record) => {
    const res = await showConfirmDlgWidget({
      title: "Delete Confirmation",
      content: `Are you sure you want to delete "${record.name}"?`,
    });
    if (res === false) return;
    deleteData(record.id);
  };

  const getUiTypeInfo = (uiType) => {
    return UI_TYPE_MAP[uiType] || { label: "Unknown", icon: <FormOutlined /> };
  };

  const renderFieldCard = (field, index) => {
    const uiTypeInfo = getUiTypeInfo(field.ui_type);
    const isRequired = field.is_required === "true";
    const isEncrypt = field.is_encrypt === "true";
    const isFilePicker = field.is_filepicker === "true";

    return (
      <div key={field.id || index} className="field-card">
        <div className="field-card-header">
          <div className="field-card-title">
            <span className="field-order">{field.order}</span>
            <span className="field-name">{field.name}</span>
            {isRequired && (
              <Tooltip title="Required">
                <CheckCircleOutlined className="field-badge required" />
              </Tooltip>
            )}
            {isEncrypt && (
              <Tooltip title="Encrypted">
                <LockOutlined className="field-badge encrypt" />
              </Tooltip>
            )}
            {isFilePicker && (
              <Tooltip title="File Picker">
                <FileOutlined className="field-badge filepicker" />
              </Tooltip>
            )}
          </div>
          <div className="field-card-actions">
            <Tooltip title="Edit">
              <EditOutlined
                className="action-icon edit"
                onClick={() => updateData(field)}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <DeleteOutlined
                className="action-icon delete"
                onClick={() => handleDelete(field)}
              />
            </Tooltip>
          </div>
        </div>
        <div className="field-card-body">
          <div className="field-info-row">
            <Tag color="blue" icon={uiTypeInfo.icon}>
              {uiTypeInfo.label}
            </Tag>
            {field.dropdown_list && field.ui_type === "3" && (
              <Tooltip title={field.dropdown_list}>
                <Tag color="purple">
                  {field.dropdown_list.split(",").length} options
                </Tag>
              </Tooltip>
            )}
          </div>
          {field.hint_text && (
            <div className="field-hint">
              <span className="hint-label">Hint:</span> {field.hint_text}
            </div>
          )}
          <div className="field-column">
            <span className="column-label">Column:</span>{" "}
            <code>{field.reg_column_name}</code>
          </div>
        </div>
      </div>
    );
  };

  const renderGroupHeader = (group) => {
    const fieldCount = group.fields.length;
    const requiredCount = group.fields.filter(
      (f) => f.is_required === "true"
    ).length;

    return (
      <div className="group-header">
        <span className="group-title">{group.title}</span>
        <div className="group-badges">
          <Badge
            count={fieldCount}
            style={{ backgroundColor: "#1890ff" }}
            title={`${fieldCount} field${fieldCount !== 1 ? "s" : ""}`}
          />
          {requiredCount > 0 && (
            <Badge
              count={`${requiredCount} req`}
              style={{ backgroundColor: "#52c41a", marginLeft: 8 }}
              title={`${requiredCount} required field${requiredCount !== 1 ? "s" : ""}`}
            />
          )}
        </div>
      </div>
    );
  };

  if (!dataList || dataList.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No form fields configured yet"
      />
    );
  }

  return (
    <div className="form-ui-grouped-view">
      <Collapse
        activeKey={activeKeys}
        onChange={setActiveKeys}
        expandIconPosition="start"
      >
        {groupedData.map((group) => (
          <Panel
            header={renderGroupHeader(group)}
            key={group.title}
            className="group-panel"
            extra={
              onAddToGroup && (
                <Tooltip title={`Add field to ${group.title}`}>
                  <PlusOutlined
                    className="add-to-group-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddToGroup(group);
                    }}
                  />
                </Tooltip>
              )
            }
          >
            <div className="fields-container">
              {group.fields.map((field, index) =>
                renderFieldCard(field, index)
              )}
            </div>
          </Panel>
        ))}
      </Collapse>
    </div>
  );
}

export default FormUiGroupedView;
